// ADDED: CSV import for the catalog. The file is parsed in the browser, every row is
// shown in an editable preview with the gaps highlighted, and nothing is sent until the
// required cells are filled in. Optionally the rows are also filed into a collection.
import React, { useState, useRef } from 'react';

import axios, { getErrorMessage } from '../../api/axios';

// The columns the catalog needs. `quantity` is the only optional one and is used
// solely when the rows are being filed into a collection.
const COLUMNS = [
    { key: 'seriesId', label: 'Series code', aliases: ['series code', 'seriesid', 'code', 'toy #', 'toy number'] },
    { key: 'name', label: 'Name', aliases: ['name', 'name on package'] },
    { key: 'brand', label: 'Make', aliases: ['make', 'brand', 'manufacturer'] },
    { key: 'model', label: 'Model', aliases: ['model', 'casting'] },
    { key: 'color', label: 'Colour', aliases: ['colour', 'color'] },
    { key: 'modelScale', label: 'Scale', aliases: ['scale', 'modelscale'] },
    { key: 'datereleased', label: 'Year', aliases: ['year', 'datereleased', 'year released'] },
    { key: 'hwcollectionName', label: 'Package series', aliases: ['package series', 'series', 'hwcollectionname', 'collection'] },
    // MODIFIED: price is optional now, so a blank cell no longer blocks the import.
    // It imports as "not priced yet" rather than as zero.
    { key: 'price', label: 'Price', aliases: ['price', 'value'], optional: true },
    { key: 'quantity', label: 'Qty', aliases: ['quantity', 'qty', 'owned'], optional: true },
];

const REQUIRED_KEYS = COLUMNS.filter((column) => !column.optional).map((column) => column.key);

// A small RFC-4180-ish parser: handles quoted fields, escaped quotes and commas inside
// quotes. Writing it here avoids pulling in a CSV dependency for one screen.
function parseCsv(text) {
    const rows = [];
    let row = [];
    let field = '';
    let inQuotes = false;

    for (let i = 0; i < text.length; i += 1) {
        const character = text[i];

        if (inQuotes) {
            if (character === '"') {
                if (text[i + 1] === '"') { field += '"'; i += 1; }
                else inQuotes = false;
            } else field += character;
            continue;
        }

        if (character === '"') { inQuotes = true; continue; }
        if (character === ',') { row.push(field); field = ''; continue; }
        if (character === '\r') continue;
        if (character === '\n') { row.push(field); rows.push(row); row = []; field = ''; continue; }
        field += character;
    }

    if (field !== '' || row.length > 0) { row.push(field); rows.push(row); }
    return rows.filter((line) => line.some((cell) => cell.trim() !== ''));
}

// Matches the file's header names to our columns, so the column order does not matter
function mapHeaders(headerRow) {
    return headerRow.map((header) => {
        const normalised = header.trim().toLowerCase();
        const match = COLUMNS.find((column) => column.key.toLowerCase() === normalised || column.aliases.includes(normalised));
        return match ? match.key : null;
    });
}

export default function CsvImportModal({ isVisible, onClose, onImported }) {
    const [rows, setRows] = useState([]);
    const [fileName, setFileName] = useState('');
    const [collections, setCollections] = useState([]);
    const [targetCollection, setTargetCollection] = useState('');
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    const [isImporting, setIsImporting] = useState(false);
    const [unmatchedHeaders, setUnmatchedHeaders] = useState([]);
    const fileInput = useRef(null);

    // The collection dropdown is loaded lazily, the first time the modal opens
    React.useEffect(() => {
        if (!isVisible) return;
        axios.get('/collections')
            .then((response) => setCollections(response.data.collections))
            .catch(() => setCollections([]));
    }, [isVisible]);

    const handleFile = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setError('');
        setResult(null);
        setFileName(file.name);

        try {
            const lines = parseCsv(await file.text());
            if (lines.length < 2) {
                setError('That file has no data rows under its header.');
                setRows([]);
                return;
            }

            const keys = mapHeaders(lines[0]);
            const unmatched = lines[0].filter((header, index) => keys[index] === null && header.trim() !== '');
            setUnmatchedHeaders(unmatched);

            if (keys.every((key) => key === null)) {
                setError('None of the column headers were recognised. Download the template below to see the expected names.');
                setRows([]);
                return;
            }

            setRows(lines.slice(1).map((line) => {
                const row = {};
                COLUMNS.forEach((column) => { row[column.key] = ''; });
                keys.forEach((key, index) => { if (key) row[key] = (line[index] || '').trim(); });
                // ADDED: codes are stored in capitals, same as the new-model form, so the
                // preview already shows what will be saved
                row.seriesId = row.seriesId.toUpperCase();
                return row;
            }));
        } catch (readError) {
            setError('Could not read that file.');
        }
    };

    const updateCell = (rowIndex, key, value) => {
        // MODIFIED: a code typed into the preview is kept in capitals too
        const cleaned = key === 'seriesId' ? value.toUpperCase() : value;
        setRows((current) => current.map((row, index) => (index === rowIndex ? { ...row, [key]: cleaned } : row)));
    };

    const isCellMissing = (row, key) => REQUIRED_KEYS.includes(key) && !String(row[key] || '').trim();
    const incompleteRows = rows.filter((row) => REQUIRED_KEYS.some((key) => isCellMissing(row, key))).length;

    const downloadTemplate = () => {
        // A header-only file, so the expected column names are never a guessing game
        const csv = `${COLUMNS.map((column) => column.label).join(',')}\n`;
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = 'speedvault-template.csv'; // MODIFIED: renamed from cloudvault-template.csv
        link.click();
        URL.revokeObjectURL(url);
    };

    const submit = async () => {
        setIsImporting(true);
        setError('');
        try {
            const response = await axios.post('/cars/import', {
                rows,
                collectionId: targetCollection || null,
            });
            setResult(response.data.results);
            if (onImported) onImported();
        } catch (err) {
            setError(getErrorMessage(err, 'The import failed.'));
        } finally {
            setIsImporting(false);
        }
    };

    const reset = () => {
        setRows([]); setFileName(''); setError(''); setResult(null);
        setUnmatchedHeaders([]); setTargetCollection('');
        if (fileInput.current) fileInput.current.value = '';
    };

    const handleClose = () => { reset(); onClose(); };

    if (!isVisible) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="w-full max-w-4xl cv-card relative max-h-[90vh] overflow-y-auto animate-fade-up">
                <div className="cv-stripe" />
                <button onClick={handleClose} aria-label="Close"
                    className="absolute top-4 right-3 cv-muted hover:text-brand-flame z-10 text-3xl leading-none px-2 transition duration-300">
                    &times;
                </button>

                <div className="px-4 sm:px-6 pb-6 pt-6">
                    <div className="text-center mb-5">
                        <p className="cv-eyebrow">Bulk add</p>
                        <h1 className="cv-display text-2xl sm:text-3xl">Import from CSV</h1>
                    </div>

                    {error && <p role="alert" className="cv-alert-error mb-4">{error}</p>}

                    {/* Step 3: the outcome */}
                    {result &&
                        <div className="space-y-3">
                            <p className="cv-alert-success">
                                {result.created} added to the catalog, {result.existing} already there
                                {result.addedToCollection > 0 && `, ${result.addedToCollection} filed into your collection`}.
                            </p>
                            {result.failed.length > 0 &&
                                <div className="cv-alert-error">
                                    <p className="font-semibold mb-1">{result.failed.length} row(s) were skipped:</p>
                                    <ul className="list-disc pl-5 space-y-0.5">
                                        {result.failed.slice(0, 8).map((failure) => (
                                            <li key={failure.row}>Row {failure.row} ({failure.label}): {failure.reason}</li>
                                        ))}
                                    </ul>
                                </div>
                            }
                            <div className="flex flex-col sm:flex-row gap-3">
                                <button onClick={reset} className="cv-btn cv-btn-outline flex-1">Import another file</button>
                                <button onClick={handleClose} className="cv-btn cv-btn-primary flex-1">Done</button>
                            </div>
                        </div>
                    }

                    {/* Step 1: pick a file */}
                    {!result && rows.length === 0 &&
                        <div className="space-y-4">
                            <div className="cv-well rounded-xl border-dashed p-8 text-center relative">
                                <input ref={fileInput} type="file" accept=".csv,text/csv" onChange={handleFile}
                                    aria-label="Choose a CSV file"
                                    className="absolute inset-0 opacity-0 cursor-pointer" />
                                <p className="cv-strong">Choose a CSV file</p>
                                <p className="text-xs cv-muted mt-1">
                                    Column order does not matter. Anything missing can be filled in on the next step.
                                </p>
                            </div>
                            <button onClick={downloadTemplate} className="cv-btn cv-btn-quiet w-full text-sm">
                                Download a blank template
                            </button>
                        </div>
                    }

                    {/* Step 2: review and fill the gaps */}
                    {!result && rows.length > 0 &&
                        <div className="space-y-4">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                <p className="text-sm cv-muted truncate">
                                    <span className="cv-strong">{fileName}</span> &middot; {rows.length} row(s)
                                </p>
                                {incompleteRows > 0
                                    ? <p className="text-sm cv-accent font-semibold">{incompleteRows} row(s) need attention</p>
                                    : <p className="text-sm cv-muted">All required fields are filled in</p>}
                            </div>

                            {unmatchedHeaders.length > 0 &&
                                <p className="text-xs cv-muted">
                                    Ignored unrecognised column(s): {unmatchedHeaders.join(', ')}
                                </p>
                            }

                            {/* Horizontal scroll rather than a squeezed table, so this stays usable on a phone */}
                            <div className="overflow-x-auto rounded-xl" style={{ border: '1px solid var(--border-subtle)' }}>
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr>
                                            <th className="px-2 py-2 text-left cv-muted font-semibold">#</th>
                                            {COLUMNS.map((column) => (
                                                <th key={column.key} className="px-2 py-2 text-left cv-muted font-semibold whitespace-nowrap">
                                                    {column.label}{!column.optional && <span className="cv-accent"> *</span>}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row, rowIndex) => (
                                            <tr key={rowIndex} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                                                <td className="px-2 py-1 cv-muted">{rowIndex + 1}</td>
                                                {COLUMNS.map((column) => (
                                                    <td key={column.key} className="px-1 py-1">
                                                        <input
                                                            value={row[column.key]}
                                                            onChange={(e) => updateCell(rowIndex, column.key, e.target.value)}
                                                            aria-label={`${column.label} for row ${rowIndex + 1}`}
                                                            placeholder={isCellMissing(row, column.key) ? 'required' : ''}
                                                            className="cv-input text-xs py-1 px-2 min-w-[7rem]"
                                                            style={isCellMissing(row, column.key)
                                                                ? { borderColor: 'var(--accent)', backgroundColor: 'var(--accent-soft)' }
                                                                : undefined}
                                                        />
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div>
                                <label className="cv-label" htmlFor="targetCollection">Also add these to a collection (optional)</label>
                                <select id="targetCollection" value={targetCollection}
                                    onChange={(e) => setTargetCollection(e.target.value)} className="cv-input">
                                    <option value="">Catalog only</option>
                                    {collections.map((collection) => (
                                        <option key={collection.id} value={collection.id}>{collection.collectionName}</option>
                                    ))}
                                </select>
                                <p className="text-xs cv-muted mt-1">The Qty column is only used when a collection is chosen.</p>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-3">
                                <button onClick={submit} disabled={isImporting || incompleteRows > 0}
                                    className="cv-btn cv-btn-primary flex-1 py-3 uppercase">
                                    {isImporting ? 'Importing...' : `Import ${rows.length} row(s)`}
                                </button>
                                <button onClick={reset} className="cv-btn cv-btn-quiet flex-1 py-3">Choose another file</button>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </div>
    );
}
